/**
 * v4.15.0 — Scheduling intelligence layer.
 *
 * Pure analysis function that takes a list of schedule blocks and
 * returns a structured report the UI can render inline. NO backend
 * dependency — this is purely the client-side intelligence that lets
 * us highlight conflicts as the operator types instead of waiting for
 * a save.
 *
 * Authoritative validation still happens server-side on apply; this
 * mirror exists so the operator never reaches "save" with a broken
 * schedule.
 */

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABEL = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

export const toMin = (hhmm) => {
    if (!hhmm) return null;
    const [h, m] = hhmm.split(':').map(n => parseInt(n, 10));
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
};
export const toHHMM = (mins) => {
    const v = Math.max(0, Math.min(24 * 60 - 1, mins));
    return `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
};
export const fmtDuration = (mins) => {
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60), m = mins % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}min`;
};
export const dayLabel = (d) => DAY_LABEL[d] || d;

/**
 * Returns:
 *   {
 *     blocks: [
 *       {
 *         index: 1,                       // 1-based for operator
 *         valid: bool,
 *         issues: [{ kind, message }],    // missing time / day / platform
 *         conflicts: [                    // overlaps with OTHER blocks
 *           {
 *             other_index, day, overlap_min,
 *             a: { startStr, endStr, platform },
 *             b: { startStr, endStr, platform },
 *             // suggested one-click fixes:
 *             fixes: [
 *               { kind: 'set_start', target_index, new_value, label },
 *               { kind: 'drop_day',  target_index, day, label },
 *             ],
 *             // first fix marked as "primary" for the inline action button.
 *           },
 *         ],
 *       },
 *     ],
 *     hasIssues: bool,
 *     hasConflicts: bool,
 *     // For TimelineStrip rendering:
 *     segments: [ { dayIdx, day, fromMin, toMin, blockIndex, platform, isCrossover } ],
 *     conflictRegions: [ { dayIdx, fromMin, toMin, indexA, indexB } ],
 *   }
 */
export function analyzeSchedule(blocks, enabled) {
    const report = {
        blocks: [],
        hasIssues: false,
        hasConflicts: false,
        segments: [],
        conflictRegions: [],
    };
    if (!enabled || !Array.isArray(blocks)) return report;

    // Per-block sanity issues.
    const perBlock = blocks.map((b, i) => {
        const idx = i + 1;
        const issues = [];
        if (!b.start_time || !b.end_time) issues.push({ kind: 'time_required', message: 'Pick a start and end time.' });
        else if (b.start_time === b.end_time) issues.push({ kind: 'time_match', message: 'Start and end can\'t match.' });
        if (!(b.days || []).length) issues.push({ kind: 'days_required', message: 'Pick at least one day.' });
        if (!b.platform) issues.push({ kind: 'platform_required', message: 'Pick a destination platform.' });
        return { index: idx, valid: issues.length === 0, issues, conflicts: [] };
    });

    // Expand each block into per-day chunks for overlap math.
    const ranges = [];
    blocks.forEach((b, i) => {
        const s = toMin(b.start_time);
        const e = toMin(b.end_time);
        if (s === null || e === null || s === e) return;
        const days = (b.days || []).map(d => String(d).toLowerCase().slice(0, 3));
        days.forEach(d => {
            if (!DAY_ORDER.includes(d)) return;
            const meta = { i: i + 1, startStr: b.start_time, endStr: b.end_time, platform: b.platform || '' };
            if (s < e) {
                ranges.push({ ...meta, day: d, s, e, isCrossover: false });
            } else {
                // Crossover — evening segment on listed day + morning chunk on next day.
                ranges.push({ ...meta, day: d, s, e: 24 * 60, isCrossover: true });
                const nextDay = DAY_ORDER[(DAY_ORDER.indexOf(d) + 1) % 7];
                ranges.push({ ...meta, day: nextDay, s: 0, e, isCrossover: true });
            }
        });
    });

    // Detect conflicts per day.
    const byDay = {};
    ranges.forEach(r => { (byDay[r.day] ||= []).push(r); });
    Object.entries(byDay).forEach(([day, items]) => {
        const sorted = [...items].sort((a, b) => a.s - b.s || a.e - b.e);
        for (let k = 1; k < sorted.length; k++) {
            const a = sorted[k - 1], b = sorted[k];
            if (a.e > b.s && a.i !== b.i) {
                const overlapMin = Math.min(a.e, b.e) - b.s;
                const dayIdx = DAY_ORDER.indexOf(day);
                const newStart = toHHMM(a.e);
                const fixes = [
                    {
                        kind: 'set_start',
                        target_index: b.i,
                        new_value: newStart,
                        label: `Move Block #${b.i} start to ${newStart}`,
                    },
                    {
                        kind: 'drop_day',
                        target_index: b.i,
                        day,
                        label: `Remove ${dayLabel(day)} from Block #${b.i}`,
                    },
                    {
                        kind: 'drop_day',
                        target_index: a.i,
                        day,
                        label: `Remove ${dayLabel(day)} from Block #${a.i}`,
                    },
                ];
                const conflict = {
                    other_index: a.i,
                    day,
                    overlap_min: overlapMin,
                    a: { startStr: a.startStr, endStr: a.endStr, platform: a.platform },
                    b: { startStr: b.startStr, endStr: b.endStr, platform: b.platform },
                    fixes,
                };
                perBlock[b.i - 1].conflicts.push(conflict);
                // Also surface mirror reference on the earlier block so operators
                // who look at #A see "conflicts with #B" too.
                perBlock[a.i - 1].conflicts.push({
                    ...conflict,
                    other_index: b.i,
                    a: { startStr: b.startStr, endStr: b.endStr, platform: b.platform },
                    b: { startStr: a.startStr, endStr: a.endStr, platform: a.platform },
                });
                report.conflictRegions.push({
                    dayIdx,
                    fromMin: b.s,
                    toMin: Math.min(a.e, b.e),
                    indexA: a.i,
                    indexB: b.i,
                });
            }
        }
    });

    // Build segments for the TimelineStrip.
    ranges.forEach(r => {
        report.segments.push({
            dayIdx: DAY_ORDER.indexOf(r.day),
            day: r.day,
            fromMin: r.s,
            toMin: r.e,
            blockIndex: r.i,
            platform: r.platform,
            isCrossover: r.isCrossover,
        });
    });

    report.blocks = perBlock;
    report.hasIssues = perBlock.some(b => b.issues.length > 0);
    report.hasConflicts = report.conflictRegions.length > 0;
    return report;
}

export const DAYS_FULL = DAY_ORDER;
export const DAY_LABEL_MAP = DAY_LABEL;
